import React from 'react';
import {
  Text,
  TouchableOpacity,
  StyleSheet,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { useGamificationShare } from './useGamificationShare';
import { shareAccent, shareAccentTint } from './shareAccent';
import type { GamificationShareType } from './shareCard';

interface Props {
  type: GamificationShareType;
  id: string;
  label?: string;
  filled?: boolean;
  compact?: boolean;
  fallbackMessage?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  onPressed?: () => void;
}

/** CORE-709 — trigger wrapper around the single gamification share controller. */
export const GamificationShareButton: React.FC<Props> = ({
  type,
  id,
  label = 'Поделиться',
  filled,
  compact,
  fallbackMessage,
  style,
  textStyle,
  onPressed,
}) => {
  const { isDark } = useTheme();
  const share = useGamificationShare({ type, id, fallbackMessage });
  // Brighter than the body accent, on a tinted disc: an outline-only trigger
  // disappeared into the painted gamification cards.
  const accent = shareAccent(isDark);

  return (
    <>
      <TouchableOpacity
        onPress={() => {
          onPressed?.();
          share.openShare();
        }}
        activeOpacity={0.85}
        style={[
          compact ? styles.iconBtn : styles.btn,
          filled
            ? { backgroundColor: accent }
            : { borderWidth: 1.5, borderColor: accent, backgroundColor: shareAccentTint(isDark) },
          style,
        ]}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <Ionicons name="share-social-outline" size={17} color={filled ? '#fff' : accent} />
        {compact ? null : (
          <Text style={[styles.txt, { color: filled ? '#fff' : accent }, textStyle]}>{label}</Text>
        )}
      </TouchableOpacity>
      {share.modal}
    </>
  );
};

const styles = StyleSheet.create({
  btn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 16, borderRadius: 999 },
  iconBtn: { alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 16 },
  txt: { fontSize: 13, fontWeight: '700' },
});
